import { OverlayModule } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import { FileUploadModule } from '@vectorsolv/vectors-ui/file-input';
import { AddressModule } from '@vsolv/core/address/web';
import { PropertiesWebModule } from '@vsolv/packages/properties/web';
import { ReimbursementWebModule } from '@vsolv/packages/reimbursement/web';
import { ModalModule } from '@vsolv/ui-kit/modal';
import { UtilsModule } from '@vsolv/ui-kit/utils';
import { AlertModule } from '@vsolv/vectors-ui/alert';
import { AppNavModule } from '@vsolv/vectors-ui/app-nav';
import { AppPageModule } from '@vsolv/vectors-ui/app-page';
import { BadgeModule } from '@vsolv/vectors-ui/badge';
import { BreadcrumbsModule } from '@vsolv/vectors-ui/breadcrumbs';
import { ButtonModule } from '@vsolv/vectors-ui/button';
import { CardModule } from '@vsolv/vectors-ui/card';
import { DatePickerModule } from '@vsolv/vectors-ui/date-picker';
import { DialogModule } from '@vsolv/vectors-ui/dialog';
import { DividerModule } from '@vsolv/vectors-ui/divider';
import { DropdownModule } from '@vsolv/vectors-ui/dropdown';
import { FormsModule } from '@vsolv/vectors-ui/forms';
import { IconsModule } from '@vsolv/vectors-ui/icons';
import { ImagePreviewModule } from '@vsolv/vectors-ui/image-preview';
import { InfoCardModule } from '@vsolv/vectors-ui/info-card';
import { InputModule } from '@vsolv/vectors-ui/input';
import { MenuModule } from '@vsolv/vectors-ui/menu';
import { RichTextEditorModule } from '@vsolv/vectors-ui/rich-text-editor';
import { SelectModule } from '@vsolv/vectors-ui/select';
import { StackGroupModule } from '@vsolv/vectors-ui/stack-group';
import { StepperModule } from '@vsolv/vectors-ui/stepper';
import { TabsModule } from '@vsolv/vectors-ui/tabs';
import { ThemingModule } from '@vsolv/vectors-ui/theming';
import { TooltipModule } from '@vsolv/vectors-ui/tooltip';
import { UserAvatarModule } from '@vsolv/vectors-ui/user-avatar';
import { VectorsUiUtilsModule } from '@vsolv/vectors-ui/utils';
import { TableModule } from '@vsolv/vectors/table';
import { CustomerWebModule } from '@wsphere/customers/web';
import { LinksWebModule } from '@wsphere/links/web';
import { ServiceProvidersWebModule } from '@wsphere/service-providers/web';
import { StaffWebModule } from '@wsphere/staff/web';
import { MomentModule } from 'ngx-moment';
import { NgPipesModule } from 'ngx-pipes';
import { ClaimLifecycleStepsModule } from '../claim-lifecycle-steps';
import { ViewDeclarationButtonModule } from '../shared';
import { WarrantyWebService } from '../warranty';
import {
  ClaimDocumentPreviewComponent,
  ClaimDocumentViewerComponent,
  ClaimItemAdjudicateComponent,
  ClaimAllocationsComponent,
  ClaimOverridesComponent,
  ClaimItemEligibilityComponent,
  ClaimAuthorizationCardComponent,
  ClaimIntakeCardComponent,
  ClaimItemCureCardComponent,
  ClaimItemCureComponent,
  ClaimItemDetailsCardComponent,
  ClaimItemDetailsComponent,
  ClaimItemLiabilityLimitComponent,
  ClaimItemLinesTableComponent,
  ClaimItemHoldsComponent,
  FileThumbComponent,
  ClaimItemResolveComponent,
  ClaimItemTableComponent,
  ClaimLifecycleComponent,
  ClaimLinkPickerComponent,
  ClaimPaymentsTableComponent,
  ClaimQuickAddComponent,
  ClaimsTableComponent,
  ClaimTimelineActivityComponent,
  ClaimTimelineComponent,
  ClaimTotalsCardComponent,
  CoveragePickerComponent,
  CoverageTooltipComponent,
} from './components';
import {
  ClaimCoverageDialog,
  ClaimItemAdjudicateDialog,
  ClaimItemCureDialog,
  ClaimAllocationDialog,
  ClaimAuthorizationDialog,
  ClaimIntakeDialog,
  ClaimItemDetailsDialog,
  ClaimItemDialog,
  ClaimItemLineDialog,
  ClaimItemLineRemoveDialog,
  ClaimItemEscalationDialog,
  ClaimItemHoldDialog,
  ClaimItemHoldCompleteDialog,
  ClaimItemLineAdjustDialog,
  ClaimItemNoteDialog,
  ClaimCloseDialog,
  ClaimItemRemoveDialog,
  ClaimItemResolveDialog,
  ClaimNoteDialog,
  ClaimPreviewPDFDialog,
  EditClaimDocumentDialog,
  ManagePaymentDialog,
  ManagePaymentMethodsDialog,
  OpenClaimDialog,
  PaymentDialog,
  UploadClaimDocumentDialog,
  ViewClaimDocumentDialog,
} from './dialogs';
import {
  ClaimGuard,
  ClaimItemAdjudicationGuard,
  ClaimItemGuard,
  ClaimItemManageGuard,
  ClaimPaymentGuard,
} from './guards';
import {
  ClaimAssetPage,
  ClaimAttachmentsPage,
  ClaimAuditPage,
  ClaimNotesPage,
  ClaimOverviewPage,
  ClaimPage,
  ClaimPaymentsPage,
  ClaimsPage,
  ClaimWorkspacePage,
} from './pages';
import { ClaimItemResolver, ClaimPaymentResolver, ClaimResolver } from './resolver';
import { ClaimItemHoldService, ClaimItemLineService, ClaimService } from './services';

@NgModule({
  providers: [
    ClaimService,
    ClaimItemLineService,
    ClaimItemHoldService,
    WarrantyWebService,
    AppPageModule,
    ClaimGuard,
    ClaimItemGuard,
    ClaimItemManageGuard,
    ClaimResolver,
    ClaimItemResolver,
    ClaimItemAdjudicationGuard,
    ClaimPaymentResolver,
    ClaimPaymentGuard,
  ],
  declarations: [
    ClaimAllocationsComponent,
    ClaimOverridesComponent,
    ClaimAllocationDialog,
    ClaimAuthorizationCardComponent,
    ClaimAuthorizationDialog,
    ClaimIntakeCardComponent,
    ClaimIntakeDialog,
    // Pages
    ClaimsPage,
    ClaimPage,
    ClaimOverviewPage,
    ClaimAssetPage,
    ClaimNotesPage,
    ClaimAttachmentsPage,
    ClaimPaymentsPage,
    ClaimAuditPage,
    ClaimWorkspacePage,

    // Components
    ClaimsTableComponent,
    ClaimItemTableComponent,
    ClaimItemLinesTableComponent,
    ClaimItemHoldsComponent,
    FileThumbComponent,
    ClaimTotalsCardComponent,
    CoverageTooltipComponent,
    ClaimLifecycleComponent,
    ClaimTimelineComponent,
    ClaimTimelineActivityComponent,
    ClaimItemDetailsComponent,
    ClaimItemDetailsCardComponent,
    ClaimItemCureComponent,
    ClaimItemCureCardComponent,
    ClaimItemAdjudicateComponent,
    ClaimItemEligibilityComponent,
    ClaimItemResolveComponent,
    ClaimItemLiabilityLimitComponent,
    ClaimQuickAddComponent,
    CoveragePickerComponent,
    ClaimPaymentsTableComponent,
    ClaimDocumentViewerComponent,
    ClaimDocumentPreviewComponent,
    ClaimLinkPickerComponent,

    // Dialogs
    OpenClaimDialog,
    ClaimItemDialog,
    ClaimCoverageDialog,
    ClaimItemDetailsDialog,
    ClaimItemCureDialog,
    ClaimItemAdjudicateDialog,
    ClaimItemResolveDialog,
    ClaimItemRemoveDialog,
    ClaimItemLineDialog,
    ClaimItemLineRemoveDialog,
    ClaimItemEscalationDialog,
    ClaimItemHoldDialog,
    ClaimItemHoldCompleteDialog,
    ClaimItemLineAdjustDialog,
    ClaimItemNoteDialog,
    ClaimCloseDialog,
    ClaimNoteDialog,
    UploadClaimDocumentDialog,
    ViewClaimDocumentDialog,
    EditClaimDocumentDialog,
    PaymentDialog,
    ManagePaymentDialog,
    ManagePaymentMethodsDialog,
    ClaimPreviewPDFDialog,
  ],
  imports: [
    CommonModule,
    UtilsModule,
    VectorsUiUtilsModule,
    ModalModule,
    NgPipesModule,
    RouterModule,
    OverlayModule,
    AppPageModule,
    TableModule,
    CardModule,
    AppNavModule,
    ButtonModule,
    ThemingModule,
    DividerModule,
    IconsModule,
    DialogModule,
    DropdownModule,
    FormsModule,
    ReactiveFormsModule,
    InputModule,
    UserAvatarModule,
    DividerModule,
    BadgeModule,
    SelectModule,
    MenuModule,
    StepperModule,
    RichTextEditorModule,
    InfoCardModule,
    ImagePreviewModule,
    ViewDeclarationButtonModule,
    StackGroupModule,
    TabsModule,
    TooltipModule,
    BreadcrumbsModule,
    AlertModule,
    AddressModule,
    MomentModule,
    MatTooltipModule,
    CustomerWebModule,
    StaffWebModule,
    ServiceProvidersWebModule,
    LinksWebModule,
    RichTextEditorModule,
    ReimbursementWebModule,
    PropertiesWebModule,
    FileUploadModule,
    ClaimLifecycleStepsModule,
    DatePickerModule,
  ],
  exports: [
    // Components
    ClaimsTableComponent,
    ClaimItemTableComponent,
    ClaimItemLinesTableComponent,
    ClaimTotalsCardComponent,
    ClaimLifecycleComponent,
    ClaimTimelineComponent,
    ClaimTimelineActivityComponent,
    ClaimPaymentsTableComponent,
    ClaimDocumentViewerComponent,
    ClaimDocumentPreviewComponent,
    ClaimLinkPickerComponent,

    // Dialogs
    OpenClaimDialog,
    ClaimCoverageDialog,
    ClaimItemDetailsDialog,
    ClaimNoteDialog,
    ViewClaimDocumentDialog,
    UploadClaimDocumentDialog,
    ManagePaymentMethodsDialog,
    ClaimItemLiabilityLimitComponent,
  ],
})
export class ClaimModule {}
